import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, IconButton, Paper, TextField, Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { WithholdingService } from '../../../services/withholding.service';
import { AuthService } from '../../../services/auth.service';
import {
  WithholdingLine, WithholdingLineInput, WithholdingRule, WithholdingType,
} from '../../../interfaces/procurement/withholding.interface';
import SearchableSelect from '../../common/SearchableSelect';
import { formatMoney } from '../../../utils/format';
import { withholdingMessageSeverity } from './warningSeverity';

export interface PaymentWithholdingValue {
  lines: WithholdingLineInput[];
  total: number;
  /** True while a re-proposal is pending (amount/bill/rule changed): the form must not submit. */
  pending: boolean;
}

interface Props {
  billId: string | null;
  /** Gross amount settled against the bill (cash + withheld). */
  amount: number | '';
  onChange: (value: PaymentWithholdingValue) => void;
}

interface EditableLine {
  key: number;
  type: WithholdingType;
  ruleId: string;
  baseAmount: number | '';
  amount: number | '';
  edited: boolean;
}

const toEditable = (l: WithholdingLine, key: number): EditableLine => ({
  key, type: l.type, ruleId: l.ruleId, baseAmount: l.baseAmount, amount: l.amount, edited: false,
});

/**
 * Withholding (VDS / TDS) deducted at payment. Renders nothing unless the institution is a
 * withholding entity. Lines are proposed from the supplier's default rules, proportionally to the
 * share of the bill being paid; each is editable (rule, base, amount) or removable.
 */
export const PaymentWithholdingSection: React.FC<Props> = ({ billId, amount, onChange }) => {
  const institutionId = AuthService.getSelectedInstitution()!;
  const [isEntity, setIsEntity] = useState(false);
  const [rules, setRules] = useState<WithholdingRule[]>([]);
  const [lines, setLines] = useState<EditableLine[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [pending, setPending] = useState(false);
  // Polish 2: false from the moment inputs change until the proposal answers, so "Withheld 0.00" is never shown as a result
  const [proposalReady, setProposalReady] = useState(false);
  const keySeq = useRef(0);
  // Requests in flight (debounced proposal + per-line rule recomputes); pending clears only at zero.
  const inflight = useRef(0);
  const begin = () => { inflight.current += 1; setPending(true); };
  const end = () => { inflight.current = Math.max(0, inflight.current - 1); if (inflight.current === 0) setPending(false); };
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const s = await WithholdingService.getSettings(institutionId);
        if (!alive || !s?.withholdingEntity) return;
        setIsEntity(true);
        const r = await WithholdingService.listRules(institutionId);
        if (alive) setRules((r ?? []).filter(x => x.active));
      } catch {
        // No access to the withholding settings: the payment simply has no withholding section.
      }
    })();
    return () => { alive = false; };
  }, [institutionId]);

  // Re-propose whenever the bill or the amount settled changes (debounced). The old lines are
  // dropped at once and `pending` blocks submission, so a stale proposal can never be posted.
  useEffect(() => {
    if (!isEntity || !billId || amount === '' || Number(amount) <= 0) {
      setLines([]);
      setWarnings([]);
      setProposalReady(true);   // nothing to compute
      return undefined;
    }
    setLines([]);
    setProposalReady(false);
    begin();
    let alive = true;
    let done = false;
    const finish = () => { if (!done) { done = true; end(); } };
    const handle = setTimeout(async () => {
      try {
        const p = await WithholdingService.getProposal(billId, Number(amount));
        if (!alive || !p) return;
        setLines(p.lines.map(l => toEditable(l, ++keySeq.current)));
        setWarnings(p.warnings ?? []);
      } catch {
        if (alive) { setLines([]); setWarnings(['Could not load the proposed withholding.']); }
      } finally {
        if (alive) setProposalReady(true);
        finish();
      }
    }, 400);
    return () => { alive = false; clearTimeout(handle); finish(); };
  }, [isEntity, billId, amount]);

  // Switching a line's rule recomputes that line from the new rule.
  const changeRule = async (key: number, ruleId: string) => {
    setLines(ls => ls.map(l => (l.key === key ? { ...l, ruleId, edited: false } : l)));
    if (!ruleId || !billId || amount === '') return;
    begin();
    try {
      const p = await WithholdingService.getProposal(billId, Number(amount), [ruleId]);
      const fresh = p?.lines?.[0];
      setLines(ls => ls.map(l => (l.key === key
        ? { ...l, ruleId, baseAmount: fresh ? fresh.baseAmount : 0, amount: fresh ? fresh.amount : 0, edited: false }
        : l)));
      if (p?.warnings?.length) setWarnings(p.warnings);
    } catch {
      setWarnings(['Could not recompute the line for the selected rule.']);
    } finally {
      end();
    }
  };

  const total = lines.reduce((s, l) => s + (l.amount === '' ? 0 : Number(l.amount)), 0);
  useEffect(() => {
    onChangeRef.current({
      total,
      pending,
      lines: lines.filter(l => l.ruleId && l.amount !== '' && Number(l.amount) > 0).map(l => ({
        type: l.type,
        ruleId: l.ruleId,
        baseAmount: l.edited && l.baseAmount !== '' ? Number(l.baseAmount) : undefined,
        amount: l.edited ? Number(l.amount) : undefined,
        edited: l.edited || undefined,
      })),
    });
  }, [lines, total, pending]);

  if (!isEntity) return null;

  const update = (key: number, patch: Partial<EditableLine>) =>
    setLines(ls => ls.map(l => (l.key === key ? { ...l, ...patch } : l)));
  const addLine = (type: WithholdingType) =>
    setLines(ls => [...ls, { key: ++keySeq.current, type, ruleId: '', baseAmount: '', amount: '', edited: false }]);
  const gross = amount === '' ? 0 : Number(amount);
  const net = gross - total;
  const calculating = pending || !proposalReady;

  return (
    <Paper data-help-section="procurement.paymentForm.section.withholding" variant="outlined" sx={{ p: 2 }} data-testid="withholding-section">
      <Typography variant="subtitle1" sx={{ mb: 1 }}>Withholding at source (VDS / TDS)</Typography>
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
        Proposed from the supplier&apos;s default rules for the share of the bill being paid. The amount
        above is the gross settled against the bill; the supplier receives it less the withholding.
      </Typography>
      {pending && <Alert severity="info" sx={{ mb: 1 }}>Recalculating withholding...</Alert>}
      {warnings.map(w => <Alert key={w} severity={withholdingMessageSeverity(w)} sx={{ mb: 1 }} data-help="procurement.payment.withholdingWarning">{w}</Alert>)}
      {lines.map(l => (
        <Box key={l.key} sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1 }}>
          <Typography sx={{ width: 40 }} variant="body2" fontWeight="bold">{l.type}</Typography>
          <Box sx={{ flex: '2 1 220px', maxWidth: 360 }}>
            <SearchableSelect<WithholdingRule>
              options={rules.filter(r => r.type === l.type)}
              value={l.ruleId}
              onChange={id => changeRule(l.key, id)}
              getOptionLabel={r => `${r.code} - ${r.ratePct}%`}
              data-help="procurement.withholdingLine.rule"
              label="Rule"
              size="small"
              clearable={false}
            />
          </Box>
          <TextField
            data-help="procurement.withholdingLine.base"
            label="Base" type="number" size="small" value={l.baseAmount}
            onChange={e => update(l.key, { baseAmount: e.target.value === '' ? '' : Number(e.target.value), edited: true })}
            inputProps={{ min: 0, step: 0.01, 'aria-label': `${l.type} base` }}
            sx={{ flex: '1 1 120px', maxWidth: 180 }}
          />
          <TextField
            data-help="procurement.withholdingLine.amount"
            label="Amount" type="number" size="small" value={l.amount}
            onChange={e => update(l.key, { amount: e.target.value === '' ? '' : Number(e.target.value), edited: true })}
            inputProps={{ min: 0, step: 0.01, 'aria-label': `${l.type} amount` }}
            sx={{ flex: '1 1 120px', maxWidth: 180 }}
          />
          <IconButton aria-label={`Remove ${l.type} line`} onClick={() => setLines(ls => ls.filter(x => x.key !== l.key))}>
            <DeleteOutlineIcon />
          </IconButton>
        </Box>
      ))}
      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
        <Button data-help="procurement.withholdingLine.addVds" size="small" onClick={() => addLine('VDS')}>Add VDS line</Button>
        <Button data-help="procurement.withholdingLine.addTds" size="small" onClick={() => addLine('TDS')}>Add TDS line</Button>
      </Box>
      <Box sx={{ backgroundColor: 'action.hover', p: 1.5, borderRadius: 1 }}>
        <Typography variant="body2">Gross settled against bill: {formatMoney(gross)}</Typography>
        {calculating ? (
          <Typography variant="body2" color="text.secondary" data-testid="withholding-calculating">
            Calculating withholding...
          </Typography>
        ) : (
          <>
            <Typography variant="body2" data-testid="withheld-total">Withheld at source: {formatMoney(total)}</Typography>
            <Typography variant="body2" fontWeight="bold" data-testid="net-cash">
              Net cash to pay supplier: {formatMoney(net)}
            </Typography>
          </>
        )}
      </Box>
    </Paper>
  );
};

export default PaymentWithholdingSection;
